import { dealerLabel } from "@/lib/naming";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFocusScroll } from "@/lib/navigation";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Loader2, Copy, Send, CheckCircle2, XCircle, Eye, Printer } from "lucide-react";
import { ProposalTaxPanel } from "@/components/proposal/ProposalTaxPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_BADGE = {
  DRAFT: "badge-neutral", SENT: "badge-reserved", VIEWED: "badge-reserved",
  ACCEPTED: "badge-available", DECLINED: "badge-sold", EXPIRED: "badge-neutral",
};
const STATUS_LABEL = {
  DRAFT: "Draft", SENT: "Sent", VIEWED: "Viewed by client",
  ACCEPTED: "Accepted", DECLINED: "Declined", EXPIRED: "Expired",
};
const EMPTY_ADDR = { line1: "", line2: "", city: "", state: "", postal_code: "" };
const addr = (a) => ({ ...EMPTY_ADDR, ...(a || {}) });
const d8 = (s) => new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric" });

function AddressFields({ value, onChange, prefix }) {
  const set = (k) => (e) => onChange({ ...value, [k]: k === "state" ? e.target.value.toUpperCase() : e.target.value });
  return (
    <div className="grid grid-cols-6 gap-2">
      <Input className="col-span-6 h-9 bg-white" placeholder="Street address" value={value.line1} onChange={set("line1")} data-testid={`${prefix}-line1`} />
      <Input className="col-span-6 h-9 bg-white" placeholder="Suite / unit (optional)" value={value.line2} onChange={set("line2")} data-testid={`${prefix}-line2`} />
      <Input className="col-span-3 h-9 bg-white" placeholder="City" value={value.city} onChange={set("city")} data-testid={`${prefix}-city`} />
      <Input className="col-span-1 h-9 bg-white" placeholder="ST" maxLength={2} value={value.state} onChange={set("state")} data-testid={`${prefix}-state`} />
      <Input className="col-span-2 h-9 bg-white" placeholder="ZIP" value={value.postal_code} onChange={set("postal_code")} data-testid={`${prefix}-zip`} />
    </div>
  );
}

export default function Proposals() {
  const [rows, setRows] = useState(null);
  const [active, setActive] = useState(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const focusId = params.get("focus");  // V5-D NAV-002 exact quote
  const focusHit = !!(focusId && rows && rows.some((p) => p.id === focusId));
  useFocusScroll(focusHit ? `proposal-row-${focusId}` : null, focusHit);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/dealer/proposals").then((r) => setRows(r.data))
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const hydrate = (data) => {
    setActive(data);
    const pf = data.prepared_for || {};
    setForm({
      shipping_usd: data.shipping_usd ?? "", terms: data.terms ?? "", availability_note: data.availability_note ?? "", expires_in_days: "",
      prepared_for: { name: pf.name || "", attention: pf.attention || "", phone: pf.phone || "", email: pf.email || "", billing_address: addr(pf.billing_address) },
      delivery_address: addr(data.delivery_address),
    });
  };
  const open = async (row) => {
    try { hydrate((await api.get(`/dealer/proposals/${row.id}`)).data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      if (data) setActive((a) => ({ ...a, ...data }));
      if (msg) toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const cleanAddr = (a) => (a.line1 || a.city || a.postal_code ? a : null);
  const save = () => act(() => {
    const payload = { prepared_for: { ...form.prepared_for, billing_address: cleanAddr(form.prepared_for.billing_address) } };
    const da = cleanAddr(form.delivery_address);
    if (da) payload.delivery_address = da;
    if (form.shipping_usd !== "") payload.shipping_usd = parseFloat(form.shipping_usd);
    if (form.terms.trim()) payload.terms = form.terms.trim();
    if (form.availability_note.trim()) payload.availability_note = form.availability_note.trim();
    if (form.expires_in_days !== "") payload.expires_in_days = parseInt(form.expires_in_days, 10);
    return api.patch(`/dealer/proposals/${active.id}`, payload);
  }, "Proposal saved");

  const copyLink = () => {
    const url = `${window.location.origin}${active.public_path}`;
    navigator.clipboard?.writeText(url).then(() => toast.success("Client link copied"));
  };
  const setPF = (patch) => setForm((f) => ({ ...f, prepared_for: { ...f.prepared_for, ...patch } }));

  return (
    <div data-testid="dealer-proposals-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Client quotes</p>
      <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Proposals</h1>
      <p className="mt-3 max-w-2xl text-sm text-warmgrey">
        Client-safe proposals generated from your projects — your pricing only, never Zelligio trade terms.
        Generate one from any project with priced lines.
      </p>

      {!rows ? (
        <Loader2 className="mt-12 h-6 w-6 animate-spin text-terracotta" />
      ) : rows.length === 0 ? (
        <Card className="mt-12 border-border bg-white">
          <CardContent className="flex flex-col items-center p-16 text-center">
            <FileText className="h-8 w-8 text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground" data-testid="proposals-empty">
              No proposals yet. Open a project, set your customer prices, and choose "Generate proposal".
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="mt-10 space-y-3">
          {rows.map((p) => (
            <Card key={p.id} id={`proposal-row-${p.id}`} className={`cursor-pointer border-border bg-white transition-all duration-200 hover:-translate-y-[1px] ${p.id === focusId ? "ring-2 ring-terracotta" : ""}`}
              onClick={() => open(p)} data-testid={`proposal-row-${p.id}`} data-focused={p.id === focusId ? "true" : undefined}>
              <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-charcoal">
                    <span className="font-mono text-xs text-muted-foreground">{p.proposal_number}</span> — {p.project_name}
                    {p.prepared_for?.name ? <span className="text-warmgrey"> · {p.prepared_for.name}</span> : null}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {p.lines.length} line{p.lines.length === 1 ? "" : "s"}
                    {p.total_usd != null ? ` · $${p.total_usd.toFixed(2)}` : p.subtotal_usd != null ? ` · $${p.subtotal_usd.toFixed(2)} subtotal` : ""}
                    {p.expires_at ? ` · valid until ${d8(p.expires_at)}` : ""}
                    {` · created ${d8(p.created_at)}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" className="h-8 text-xs text-charcoal" onClick={(e) => { e.stopPropagation(); navigate(`/dealer/proposals/${p.id}/view`); }} data-testid={`proposal-view-${p.id}`}>
                    <Eye className="mr-1 h-3.5 w-3.5" /> View
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 text-xs text-charcoal" onClick={(e) => { e.stopPropagation(); window.open(`${API}/dealer/proposals/${p.id}/pdf`, "_blank", "noopener"); }} data-testid={`proposal-print-${p.id}`}>
                    <Printer className="mr-1 h-3.5 w-3.5" /> Print
                  </Button>
                  <span className={`badge-status ${STATUS_BADGE[p.status] || "badge-neutral"}`} data-testid={`proposal-status-${p.id}`}>
                    {STATUS_LABEL[p.status] || p.status}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="flex max-h-[92dvh] w-[calc(100vw-1.5rem)] flex-col gap-0 overflow-hidden border-border bg-white p-0 sm:max-w-3xl">
          {active && form && (
            <>
              <DialogHeader className="shrink-0 border-b border-border px-6 pt-6 pb-4 pr-12">
                <DialogTitle className="font-display text-2xl font-medium text-charcoal">
                  {active.proposal_number} — {active.project_name}
                </DialogTitle>
                <DialogDescription className="text-sm text-warmgrey">
                  Client-safe proposal. Only your selling prices appear — no Zelligio trade terms.
                </DialogDescription>
                <div className="flex flex-wrap gap-2 pt-2" data-testid="proposal-actions">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/dealer/proposals/${active.id}/view`)} data-testid="proposal-view-button" className="h-8 border-limestone text-xs text-charcoal hover:bg-sand"><Eye className="mr-1 h-3.5 w-3.5" /> View proposal</Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(`${API}/dealer/proposals/${active.id}/pdf`, "_blank", "noopener")} data-testid="proposal-print-button" className="h-8 border-limestone text-xs text-charcoal hover:bg-sand"><Printer className="mr-1 h-3.5 w-3.5" /> Print / PDF</Button>
                  {active.status !== "DRAFT" && (
                    <Button size="sm" variant="outline" onClick={copyLink} data-testid="proposal-copy-link" className="h-8 border-limestone text-xs text-charcoal hover:bg-sand"><Copy className="mr-1 h-3.5 w-3.5" /> Copy link</Button>
                  )}
                </div>
              </DialogHeader>

              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-2" data-testid="proposal-lines">
                {active.lines.map((l, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 rounded-md border border-border bg-sand/40 px-3 py-2.5 text-sm" data-testid={`proposal-line-${i}`}>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-charcoal">{dealerLabel(l)}</p>
                      <p className="text-xs text-muted-foreground">
                        {l.boxes} boxes{l.coverage_sqft ? ` · ${l.coverage_sqft} sq ft` : ""} · ${l.customer_price_per_box?.toFixed(2)}/box
                        {l.customer_price_per_sqft != null ? ` ($${l.customer_price_per_sqft.toFixed(2)}/sq ft)` : ""}
                      </p>
                    </div>
                    <span className="num shrink-0 text-charcoal">${l.line_total?.toFixed(2)}</span>
                  </div>
                ))}
                <div className="space-y-1 pt-2 text-sm">
                  <div className="flex justify-between"><span className="text-warmgrey">Subtotal</span><span className="num text-charcoal" data-testid="proposal-subtotal">${active.subtotal_usd?.toFixed(2)}</span></div>
                  {active.shipping_usd != null && <div className="flex justify-between"><span className="text-warmgrey">Shipping</span><span className="num text-charcoal">${active.shipping_usd.toFixed(2)}</span></div>}
                  <ProposalTaxPanel proposal={active} editable={active.status === "DRAFT"} onChange={(data) => { setActive((a) => ({ ...a, ...data })); load(); }} />
                  <div className="flex justify-between border-t border-border pt-2"><span className="font-medium text-charcoal">Total</span>
                    <span className="num font-semibold text-charcoal" data-testid="proposal-total">{active.total_usd != null ? `$${active.total_usd.toFixed(2)}` : "Pending tax"}</span></div>
                </div>
              </div>

              {active.status === "DRAFT" ? (
                <div className="mt-4 space-y-4 border-t border-border pt-4" data-testid="proposal-edit-form">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-terracotta">Prepared for</p>
                    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <Input className="h-9 bg-white" placeholder="Customer company / name *" value={form.prepared_for.name} onChange={(e) => setPF({ name: e.target.value })} data-testid="proposal-customer-name" />
                      <Input className="h-9 bg-white" placeholder="Attention to" value={form.prepared_for.attention} onChange={(e) => setPF({ attention: e.target.value })} data-testid="proposal-customer-attention" />
                      <Input className="h-9 bg-white" placeholder="Phone" value={form.prepared_for.phone} onChange={(e) => setPF({ phone: e.target.value })} data-testid="proposal-customer-phone" />
                      <Input className="h-9 bg-white" placeholder="Email" type="email" value={form.prepared_for.email} onChange={(e) => setPF({ email: e.target.value })} data-testid="proposal-customer-email" />
                    </div>
                    <Label className="mt-3 block text-xs text-warmgrey">Billing address</Label>
                    <div className="mt-1"><AddressFields prefix="proposal-billing" value={form.prepared_for.billing_address} onChange={(a) => setPF({ billing_address: a })} /></div>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-terracotta">Project / delivery address</p>
                    <p className="mt-1 text-xs text-warmgrey">Sales tax is calculated for this destination (billing address is used if empty).</p>
                    <div className="mt-2"><AddressFields prefix="proposal-delivery" value={form.delivery_address} onChange={(a) => setForm((f) => ({ ...f, delivery_address: a }))} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs text-warmgrey">Shipping $</Label><Input type="number" min="0" step="0.01" value={form.shipping_usd} onChange={(e) => setForm((f) => ({ ...f, shipping_usd: e.target.value }))} data-testid="proposal-shipping" className="mt-1 h-10 bg-white" /></div>
                    <div><Label className="text-xs text-warmgrey">Valid for (days)</Label><Input type="number" min="1" max="180" value={form.expires_in_days} onChange={(e) => setForm((f) => ({ ...f, expires_in_days: e.target.value }))} placeholder={active.expires_at ? `until ${d8(active.expires_at)}` : "e.g. 30"} data-testid="proposal-expires" className="mt-1 h-10 bg-white" /></div>
                  </div>
                  <div><Label className="text-xs text-warmgrey">Availability note</Label><Input value={form.availability_note} onChange={(e) => setForm((f) => ({ ...f, availability_note: e.target.value }))} placeholder="e.g. In stock — ships within 5 business days" data-testid="proposal-availability" className="mt-1 h-10 bg-white" /></div>
                  <div><Label className="text-xs text-warmgrey">Payment terms</Label><Textarea value={form.terms} onChange={(e) => setForm((f) => ({ ...f, terms: e.target.value }))} placeholder="e.g. 50% deposit to reserve material, balance due before shipment. Prices valid through the date above." data-testid="proposal-terms" className="mt-1 min-h-[70px] bg-white" /></div>
                  <div className="flex flex-wrap gap-3 pt-1">
                    <Button onClick={save} disabled={busy} variant="outline" data-testid="proposal-save" className="border-limestone text-charcoal hover:bg-sand">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save draft"}
                    </Button>
                    <Button onClick={() => act(() => api.post(`/dealer/proposals/${active.id}/send`), "Proposal ready — share the client link")}
                      disabled={busy} data-testid="proposal-send" className="bg-terracotta text-white hover:bg-terracotta-hover">
                      <Send className="mr-2 h-4 w-4" /> Finalize & get client link
                    </Button>
                  </div>
                  {!active.expires_at && form.expires_in_days === "" && (
                    <p className="text-xs text-[#8a6435]" data-testid="proposal-expiry-hint">Set a validity period (days) before finalizing.</p>
                  )}
                </div>
              ) : (
                <div className="mt-4 space-y-3 border-t border-border pt-4">
                  {active.public_path && (
                    <div className="flex flex-wrap items-center gap-2" data-testid="proposal-link-row">
                      <code className="min-w-0 flex-1 truncate rounded-md border border-border bg-sand px-3 py-2 text-xs text-charcoal" data-testid="proposal-public-link">
                        {`${window.location.origin}${active.public_path}`}
                      </code>
                      <Button size="sm" variant="outline" onClick={copyLink} data-testid="proposal-copy-link-row" className="border-limestone text-charcoal hover:bg-sand">
                        <Copy className="mr-1 h-3.5 w-3.5" /> Copy
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {active.sent_at ? `Sent ${d8(active.sent_at)}` : ""}
                    {active.viewed_at ? ` · viewed ${d8(active.viewed_at)}` : ""}
                    {active.expires_at ? ` · valid until ${d8(active.expires_at)}` : ""}
                  </p>
                  {["SENT", "VIEWED"].includes(active.status) && (
                    <div className="flex flex-wrap gap-3">
                      <Button size="sm" onClick={() => act(() => api.post(`/dealer/proposals/${active.id}/status`, { status: "ACCEPTED" }), "Marked accepted")}
                        disabled={busy} data-testid="proposal-accept" className="bg-status-available text-white hover:opacity-90">
                        <CheckCircle2 className="mr-1 h-4 w-4" /> Mark accepted
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/dealer/proposals/${active.id}/status`, { status: "DECLINED" }), "Marked declined")}
                        disabled={busy} data-testid="proposal-decline" className="border-status-sold text-status-sold hover:bg-sand">
                        <XCircle className="mr-1 h-4 w-4" /> Mark declined
                      </Button>
                    </div>
                  )}
                </div>
              )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
